import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import type { ReactNode } from 'react'
import { Divider } from '@sunwebgroup/uds-ui/divider'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import styles from './AuthConfirmationLayout.module.css'

export interface AuthConfirmationLayoutProps {
  icon?: IconGlyph
  title: string
  children: ReactNode
  afterNote?: ReactNode
  /**
   * Shows the divider and the spam folder note below the content.
   * @default true
   */
  showSpamNote?: boolean
  /**
   * Live region role. Use `alert` for errors so screen readers announce them immediately.
   * @default 'status'
   */
  role?: 'status' | 'alert'
}

export function AuthConfirmationLayout({ icon, title, children, afterNote, showSpamNote = true, role = 'status' }: AuthConfirmationLayoutProps) {
  const intl = useIntl()

  return (
    <section className={styles.confirmation} role={role} aria-live={role === 'alert' ? 'assertive' : 'polite'}>
      {icon && <Icon glyph={icon} size={32} aria-hidden="true" />}
      <Typography as="h1" className={styles.title}>
        {title}
      </Typography>
      {children}
      {showSpamNote && (
        <>
          <Divider className={styles.divider} />
          <Typography as="p" variant="body-s-regular-tight" className={styles.note}>
            {intl.formatMessage({ id: 'auth_confirmation_spam_note' })}
          </Typography>
        </>
      )}
      {afterNote}
    </section>
  )
}
